import { CaipNudgeTriggerEvent } from "../../types/CaipNudgeTriggerEvent";
import { CellType } from "../../types/CellType";
import { EditorStatus } from "../../types/EditorStatus";
import { getStore } from "../store/appStore";
import { AppAction } from "../types";
import { EditorActionKey as ActionKey } from "./ActionKeys";

interface EditorErrorOccurredAction
  extends AppAction<
    ActionKey.EDITOR_ERROR,
    { error: string | null; status: EditorStatus }
  > {}
interface SetEditorKernelIsRunningAction
  extends AppAction<
    ActionKey.SET_EDITOR_KERNEL_IS_RUNNING,
    { isRunning: boolean }
  > {}
interface SetEditorSelectedCellTypeAction
  extends AppAction<
    ActionKey.SET_EDITOR_SELECTED_CELL_TYPE,
    { type: CellType }
  > {}
interface SetEditorMenuAction
  extends AppAction<ActionKey.SET_EDITOR_MENU, { menuPath: string[] }> {}
interface PushEditorMenuAction
  extends AppAction<ActionKey.PUSH_EDITOR_MENU, { menuName: string }> {}
interface PopEditorMenuAction
  extends AppAction<ActionKey.POP_EDITOR_MENU, {}> {}
interface ClearEditorMenuAction
  extends AppAction<ActionKey.CLEAR_EDITOR_MENU, {}> {}
interface SetEditorStatusAction
  extends AppAction<ActionKey.SET_EDITOR_STATUS, { status: EditorStatus }> {}
interface SetNonfatalErrorMessageAction
  extends AppAction<
    ActionKey.SET_NONFATAL_ERROR_MESSAGE,
    { message: string | null }
  > {}
interface SetEditorCaipNudgeTriggerEvent
  extends AppAction<
    ActionKey.SET_EDITOR_CAIP_NUDGE_TRIGGER_EVENT,
    { trigger: CaipNudgeTriggerEvent | null }
  > {}

export type EditorAction =
  | EditorErrorOccurredAction
  | SetEditorKernelIsRunningAction
  | SetEditorSelectedCellTypeAction
  | SetEditorMenuAction
  | PushEditorMenuAction
  | PopEditorMenuAction
  | ClearEditorMenuAction
  | SetEditorStatusAction
  | SetNonfatalErrorMessageAction
  | SetEditorCaipNudgeTriggerEvent;

/** Set the status of the editor (mutually exclusive). */
export const setEditorStatus = (status: EditorStatus): SetEditorStatusAction =>
  getStore().dispatch({
    type: ActionKey.SET_EDITOR_STATUS,
    payload: { status },
  });

export const clearEditorError = (): EditorErrorOccurredAction =>
  getStore().dispatch({
    type: ActionKey.EDITOR_ERROR,
    payload: { error: null, status: EditorStatus.ERROR },
  });

export const editorErrorOccurred = (
  error: string,
  status: EditorStatus = EditorStatus.ERROR
): EditorErrorOccurredAction =>
  getStore().dispatch({
    type: ActionKey.EDITOR_ERROR,
    payload: { error, status },
  });

export const userErrorOccurred = (error: string): EditorErrorOccurredAction =>
  getStore().dispatch({
    type: ActionKey.EDITOR_ERROR,
    payload: { error, status: EditorStatus.STOPPED },
  });

export const editorKernelBecameBusy = (): SetEditorKernelIsRunningAction =>
  getStore().dispatch({
    type: ActionKey.SET_EDITOR_KERNEL_IS_RUNNING,
    payload: { isRunning: true },
  });

export const editorKernelBecameIdle = (): SetEditorKernelIsRunningAction =>
  getStore().dispatch({
    type: ActionKey.SET_EDITOR_KERNEL_IS_RUNNING,
    payload: { isRunning: false },
  });

/** Changes the active editor menu path. */
export const setEditorMenu = (menuPath: string[]): SetEditorMenuAction =>
  getStore().dispatch({
    type: ActionKey.SET_EDITOR_MENU,
    payload: { menuPath },
  });

/** Opens a menu / submenu onto the active path. */
export const pushEditorMenu = (menuName: string): PushEditorMenuAction =>
  getStore().dispatch({
    type: ActionKey.PUSH_EDITOR_MENU,
    payload: { menuName },
  });

/** Pops a menu / submenu from the active path. */
export const popEditorMenu = (): PopEditorMenuAction =>
  getStore().dispatch({
    type: ActionKey.POP_EDITOR_MENU,
    payload: {},
  });

/** Clears the menu path (closes all menus). */
export const clearEditorMenu = (): ClearEditorMenuAction =>
  getStore().dispatch({
    type: ActionKey.CLEAR_EDITOR_MENU,
    payload: {},
  });

export const setNonfatalErrorMessage = (
  message: string
): SetNonfatalErrorMessageAction =>
  getStore().dispatch({
    type: ActionKey.SET_NONFATAL_ERROR_MESSAGE,
    payload: { message },
  });

export const setEditorCaipNudgeTriggerEvent = (
  trigger: CaipNudgeTriggerEvent
): SetEditorCaipNudgeTriggerEvent =>
  getStore().dispatch({
    type: ActionKey.SET_EDITOR_CAIP_NUDGE_TRIGGER_EVENT,
    payload: { trigger },
  });
export const unsetEditorCaipNudgeTriggerEvent =
  (): SetEditorCaipNudgeTriggerEvent =>
    getStore().dispatch({
      type: ActionKey.SET_EDITOR_CAIP_NUDGE_TRIGGER_EVENT,
      payload: { trigger: null },
    });
